import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { OptionCard, Banner } from "../../components/Controls";
import { audit } from "../../lib/api";
import { QUESTIONS, SECTION_INTERSTITIALS, visibleQuestions, getOptionsFor } from "../../data/questions";

const SECTIONS = ["A", "B", "C"];

export default function Plan2Questionnaire() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || localStorage.getItem("cw_audit_token") || "";

  const [answers, setAnswers] = useState({});
  const [otherText, setOtherText] = useState({}); // { [qkey]: string }
  const [sector, setSector] = useState("Other");
  const [loading, setLoading] = useState(true);
  const [banner, setBanner] = useState(null);
  const [current, setCurrent] = useState(0);
  const [interstitialShown, setInterstitialShown] = useState({ A: false, B: false, C: false });

  useEffect(() => {
    if (!token) { nav("/plan-2/gate"); return; }
    (async () => {
      try {
        const res = await audit.state(token);
        setAnswers(res.answered || {});
        setSector(res.lead.sector || "Other");
        // Reconstruct otherText from any *_other_text responses
        const others = {};
        Object.entries(res.answered || {}).forEach(([k, v]) => {
          if (k.endsWith("_other_text")) others[k.replace("_other_text", "")] = v;
        });
        setOtherText(others);
        const visible = visibleQuestions(res.answered || {});
        const firstUnansweredIdx = visible.findIndex((q) => (res.answered || {})[q.key] === undefined);
        setCurrent(firstUnansweredIdx === -1 ? visible.length : firstUnansweredIdx);
        setLoading(false);
      } catch (err) {
        setBanner({ kind: "error", text: "Your session has expired. Return to the start to request a new link." });
        setLoading(false);
      }
    })();
  }, [token, nav]);

  const flow = useMemo(() => visibleQuestions(answers), [answers]);
  const totalVisible = flow.length;

  const saveAnswer = async (key, val, otherFreeText) => {
    setAnswers((a) => ({ ...a, [key]: val }));
    try {
      await audit.answer(token, key, val);
      if (otherFreeText !== undefined) {
        await audit.answer(token, `${key}_other_text`, otherFreeText);
        setOtherText((o) => ({ ...o, [key]: otherFreeText }));
      }
      setBanner(null);
    } catch (err) {
      setBanner({ kind: "error", text: "We are having trouble saving your answers. Check your connection; your progress up to the last saved answer is safe." });
      throw err;
    }
  };

  const goNext = () => setCurrent((c) => c + 1);
  const goBack = () => setCurrent((c) => Math.max(0, c - 1));

  const finish = async () => {
    try {
      await audit.submit(token);
      localStorage.removeItem("cw_audit_token");
      nav("/plan-2/submitted");
    } catch (err) {
      setBanner({ kind: "error", text: "Could not submit right now. Please try again." });
    }
  };

  if (loading) {
    return (
      <div className="p2-assessment cw-assessment cw-flow" data-testid="questionnaire-loading">
        <div className="cw-flow-column">Loading&hellip;</div>
      </div>
    );
  }

  const questionAtCurrent = flow[current];
  const showInterstitial = (() => {
    if (!questionAtCurrent) return null;
    const section = questionAtCurrent.section;
    if (interstitialShown[section]) return null;
    const idx = flow.findIndex((q) => q.section === section);
    if (idx !== current) return null;
    return section;
  })();

  const activeSection = questionAtCurrent ? questionAtCurrent.section : "C";

  return (
    <div className="p2-assessment cw-assessment cw-flow" data-testid="questionnaire-page">
      <ProgressTracker
        current={current + 1}
        total={totalVisible}
        activeSection={activeSection}
        answers={answers}
        flow={flow}
      />
      <div className="cw-flow-column">
        {banner && (
          <div style={{ marginBottom: 24 }}>
            <Banner kind={banner.kind}>{banner.text}</Banner>
          </div>
        )}

        {showInterstitial && (
          <Interstitial
            section={showInterstitial}
            onContinue={() => setInterstitialShown((s) => ({ ...s, [showInterstitial]: true }))}
          />
        )}

        {!showInterstitial && questionAtCurrent && (
          <QuestionScreen
            key={questionAtCurrent.key}
            question={questionAtCurrent}
            sector={sector}
            value={answers[questionAtCurrent.key]}
            otherText={otherText[questionAtCurrent.key] || ""}
            onSave={async (val, otherFreeText) => { await saveAnswer(questionAtCurrent.key, val, otherFreeText); goNext(); }}
            onBack={current > 0 ? goBack : null}
            onSkip={questionAtCurrent.optional ? () => saveAnswer(questionAtCurrent.key, "").then(goNext) : null}
          />
        )}

        {!showInterstitial && !questionAtCurrent && (
          <FinishScreen onSubmit={finish} onBack={() => setCurrent(flow.length - 1)} />
        )}
      </div>
    </div>
  );
}

function ProgressTracker({ current, total, activeSection, flow }) {
  const activeIdx = SECTIONS.indexOf(activeSection);
  const pct = Math.max(0, Math.min(100, (current / Math.max(total, 1)) * 100));
  const sectionCounts = SECTIONS.map((s) => flow.filter((q) => q.section === s).length);
  return (
    <div className="cw-progress-wrap" data-testid="progress-tracker">
      <div className="cw-progress-inner">
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          {SECTIONS.map((s, i) => (
            <div key={s} style={{ display: "flex", alignItems: "center", gap: 8 }} data-testid={`section-pip-${s}`}>
              <span
                className="cw-label"
                style={{
                  color: i <= activeIdx ? "var(--cw-midnight)" : "rgba(22,30,27,0.35)",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {String(i + 1).padStart(2, "0")} &middot; Section {s}
              </span>
              <span
                aria-hidden
                style={{
                  width: 6, height: 6, borderRadius: 999,
                  background: i < activeIdx ? "var(--cw-seafoam)" :
                             i === activeIdx ? "var(--cw-marigold)" :
                             "rgba(22,30,27,0.18)",
                  transition: "background-color 300ms var(--ease-out-strong)",
                }}
              />
            </div>
          ))}
        </div>
        <div style={{ flex: 1 }} />
        <div className="cw-label cw-tabular" data-testid="progress-label" style={{ whiteSpace: "nowrap" }}>
          <span style={{ color: "var(--cw-midnight)" }}>{String(current).padStart(2, "0")}</span>
          <span style={{ margin: "0 6px", opacity: 0.5 }}>/</span>
          <span>{String(total).padStart(2, "0")}</span>
        </div>
      </div>
      <div style={{ maxWidth: 720, margin: "10px auto 0" }}>
        <div className="cw-progress-track">
          <div className="cw-progress-fill" style={{ width: `${pct}%` }} />
        </div>
      </div>
    </div>
  );
}

function Interstitial({ section, onContinue }) {
  const meta = SECTION_INTERSTITIALS[section];
  const idx = { A: "01", B: "02", C: "03" }[section] || "01";
  return (
    <div className="cw-fade-in" data-testid={`interstitial-${section}`}>
      <div className="cw-eyebrow">Section {section} of 3</div>
      <hr className="cw-rule cw-rule-marigold" style={{ marginTop: 20 }} />
      <div className="cw-landing-card-number" style={{ marginTop: 8 }}>{idx}</div>
      <h1 className="cw-display-l" style={{ marginTop: 8 }} data-testid={`interstitial-heading-${section}`}>
        {meta.heading}
      </h1>
      <p className="cw-body" style={{ marginTop: 24, color: "rgba(22,30,27,0.82)", maxWidth: 560 }} data-testid={`interstitial-body-${section}`}>
        {meta.body}
      </p>
      <button
        className="cw-btn cw-btn-primary"
        onClick={onContinue}
        style={{ marginTop: 48 }}
        data-testid={`interstitial-continue-${section}`}
      >
        {meta.cta}
      </button>
    </div>
  );
}

function QuestionScreen({ question, sector, value, otherText, onSave, onBack, onSkip }) {
  const options = getOptionsFor(question, sector);

  // Local state
  const [localMulti, setLocalMulti] = useState(Array.isArray(value) ? value : []);
  const [text, setText] = useState(typeof value === "string" && question.type === "text" ? value : "");
  const [singlePick, setSinglePick] = useState(typeof value === "string" && question.type === "single" ? value : "");
  const [otherLocal, setOtherLocal] = useState(otherText || "");
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    setLocalMulti(Array.isArray(value) ? value : []);
    setText(typeof value === "string" && question.type === "text" ? value : "");
    setSinglePick(typeof value === "string" && question.type === "single" ? value : "");
    setOtherLocal(otherText || "");
    setInvalid(false);
  }, [question.key, value, otherText, question.type]);

  const singleOtherAwaitingText = question.hasOther && singlePick === "Other";
  const multiOtherSelected = question.hasOther && localMulti.includes("Other");

  const handleSingle = (opt) => {
    setSinglePick(opt);
    if (opt === "Other" && question.hasOther) {
      // do not auto-advance; wait for user to type + Continue
      return;
    }
    setTimeout(() => onSave(opt), 250);
  };

  const continueSingleOther = () => {
    if (!otherLocal.trim()) { setInvalid(true); return; }
    onSave("Other", otherLocal.trim());
  };

  const toggleMulti = (opt) => {
    setInvalid(false);
    let next;
    if (question.exclusive && opt === question.exclusive) {
      next = localMulti.includes(opt) ? [] : [opt];
    } else if (question.exclusive && localMulti.includes(question.exclusive)) {
      next = [opt];
    } else {
      next = localMulti.includes(opt) ? localMulti.filter((o) => o !== opt) : [...localMulti, opt];
    }
    setLocalMulti(next);
  };

  const canContinueMulti = () => {
    if (question.minSelected && localMulti.length < question.minSelected) return false;
    if (!localMulti.length && !question.exclusive) return false;
    if (multiOtherSelected && !otherLocal.trim()) return false;
    return true;
  };

  const submitMulti = () => {
    if (!canContinueMulti()) { setInvalid(true); return; }
    onSave(localMulti, multiOtherSelected ? otherLocal.trim() : undefined);
  };

  return (
    <div className="cw-fade-in" data-testid={`question-${question.key}`}>
      <div className="cw-question-number">
        {question.key.replace("Q", "Question ")} &middot; Section {question.section}
      </div>
      <hr className="cw-rule" style={{ marginTop: 16 }} />
      <h1 className="cw-display-l" data-testid={`question-prompt-${question.key}`}>
        {question.prompt}
      </h1>
      {question.helper && (
        <p className="cw-small" style={{ marginTop: 12, color: "rgba(22,30,27,0.60)" }} data-testid={`question-helper-${question.key}`}>
          {question.helper}
        </p>
      )}

      {question.type === "single" && (
        <>
          <div className="cw-stagger" style={{ display: "grid", gap: 10, marginTop: 40 }}>
            {options.map((opt) => (
              <OptionCard
                key={opt}
                label={opt}
                selected={singlePick === opt}
                onClick={() => handleSingle(opt)}
                testid={`opt-${question.key}-${opt.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
              />
            ))}
          </div>
          {singleOtherAwaitingText && (
            <div style={{ marginTop: 24 }} className="cw-fade-in" data-testid={`other-input-${question.key}`}>
              <div className="cw-label" style={{ marginBottom: 8 }}>Please specify</div>
              <input
                className={`cw-input ${invalid ? "error" : ""}`}
                placeholder="Type your answer"
                value={otherLocal}
                onChange={(e) => { setOtherLocal(e.target.value); setInvalid(false); }}
                data-testid={`other-input-field-${question.key}`}
                autoFocus
              />
              {invalid && (
                <p className="cw-small" style={{ color: "var(--cw-error)", marginTop: 8 }}>Please add a short answer.</p>
              )}
              <button
                className="cw-btn cw-btn-primary"
                style={{ marginTop: 20 }}
                onClick={continueSingleOther}
                data-testid={`continue-${question.key}`}
              >Continue</button>
            </div>
          )}
        </>
      )}

      {question.type === "multi" && (
        <>
          <div className="cw-stagger" style={{ display: "grid", gap: 10, marginTop: 40 }}>
            {options.map((opt) => {
              const isExclusive = question.exclusive && opt === question.exclusive;
              const anyExclusive = question.exclusive && localMulti.includes(question.exclusive);
              const disabled = !isExclusive && anyExclusive;
              return (
                <OptionCard
                  key={opt}
                  label={opt}
                  selected={localMulti.includes(opt)}
                  multi
                  disabled={disabled}
                  onClick={() => toggleMulti(opt)}
                  testid={`opt-${question.key}-${opt.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                />
              );
            })}
          </div>
          {multiOtherSelected && (
            <div style={{ marginTop: 20 }} className="cw-fade-in" data-testid={`other-input-${question.key}`}>
              <div className="cw-label" style={{ marginBottom: 8 }}>Please specify (Other)</div>
              <input
                className={`cw-input ${invalid && !otherLocal.trim() ? "error" : ""}`}
                placeholder="Type your answer"
                value={otherLocal}
                onChange={(e) => { setOtherLocal(e.target.value); setInvalid(false); }}
                data-testid={`other-input-field-${question.key}`}
              />
            </div>
          )}
          {invalid && (
            <p className="cw-small" style={{ color: "var(--cw-error)", marginTop: 12 }} data-testid="multi-select-min-error">
              {multiOtherSelected && !otherLocal.trim()
                ? "Please add a short answer for Other."
                : "Select at least one."}
            </p>
          )}
          <button
            className="cw-btn cw-btn-primary"
            style={{ marginTop: 24 }}
            data-testid={`continue-${question.key}`}
            onClick={submitMulti}
            disabled={!canContinueMulti()}
          >
            Continue
          </button>
        </>
      )}

      {question.type === "text" && (
        <>
          <textarea
            className="cw-input"
            style={{ height: "auto", padding: "18px 22px", borderRadius: 16, minHeight: 140, resize: "vertical" }}
            placeholder={question.placeholder}
            value={text}
            onChange={(e) => setText(e.target.value)}
            data-testid={`text-${question.key}`}
          />
          <div style={{ display: "flex", gap: 16, alignItems: "center", marginTop: 24, flexWrap: "wrap" }}>
            <button
              className="cw-btn cw-btn-primary"
              onClick={() => onSave(text.trim())}
              data-testid={`continue-${question.key}`}
            >Continue</button>
            {onSkip && (
              <button
                className="cw-btn-text"
                onClick={onSkip}
                data-testid={`skip-${question.key}`}
              >Skip this question</button>
            )}
          </div>
        </>
      )}

      {onBack && (
        <div style={{ marginTop: 48 }}>
          <button className="cw-btn-text" onClick={onBack} data-testid={`back-${question.key}`}>
            &larr; Back
          </button>
        </div>
      )}
    </div>
  );
}

function FinishScreen({ onSubmit, onBack }) {
  return (
    <div className="cw-fade-in" data-testid="finish-screen">
      <div className="cw-eyebrow">Complete</div>
      <hr className="cw-rule" style={{ marginTop: 20 }} />
      <h1 className="cw-display-l">Ready to submit your audit?</h1>
      <p className="cw-body" style={{ marginTop: 20, color: "rgba(22,30,27,0.82)", maxWidth: 540 }}>
        Once submitted, your Crowther AI Strategy Audit is scored, assembled, and delivered to your
        inbox within the hour. You can close this page after submitting.
      </p>
      <div style={{ marginTop: 40, display: "flex", gap: 16, flexWrap: "wrap" }}>
        <button
          className="cw-btn cw-btn-primary"
          onClick={onSubmit}
          data-testid="submit-final-btn"
        >
          Submit and generate my report
        </button>
        {onBack && (
          <button className="cw-btn-text" onClick={onBack} data-testid="finish-back-btn">
            &larr; Review answers
          </button>
        )}
      </div>
    </div>
  );
}
